import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { Tradable } from "@models/interfaces/tradable";
import { Index } from "@models/asset/index/index.model";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Helper } from "@helpers/functions";
import { BpOption } from "@models/ui-components/bp-option";
import { map } from "rxjs/operators";
import { AssetGroupName } from "@modules/+new/shared/const/enums/asset-group-name.enum";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";

@Injectable()
export class AssetListSellService {
	private tradableList$: BehaviorSubject<Tradable[]> = new BehaviorSubject<Tradable[]>([]);
	private allTradables: Tradable[] = [];

	constructor(
		protected tradableListService: TradableListService,
		protected tradableWalletListService: TradableWalletListService,
		protected launchDarklyService: LaunchDarklyService
	) {}

	public initList(isLimitOrder: boolean = false): void {
		const list = this.getSellTradables(isLimitOrder);
		this.allTradables = [...list];
		this.tradableList$.next(list);
	}

	public getList$(): Observable<Tradable[]> {
		//add security token filter
		//TODO: remove security token filter when ff is removed
		return combineLatest({
			tradables: this.tradableList$
				.asObservable()
				.pipe(
					map((assets) =>
						assets.filter((cashPlus) => cashPlus.getAssetGroupName() !== AssetGroupName.FiatEarn)
					)
				),
			isSecurityTokenCategoryActive: this.launchDarklyService.isActive$(LD_FLAG.SECURITY_TOKENS)
		}).pipe(
			map(({ tradables, isSecurityTokenCategoryActive }) => {
				if (!isSecurityTokenCategoryActive) {
					return tradables.filter((f) => !(f instanceof SecurityToken));
				}

				return tradables;
			})
		);
	}

	public getFilters(filters: BpOption[], tradables: Tradable[]): BpOption[] {
		let currencyTypes = tradables.map((t) => t.getCurrencyType()); // get all currency types (could also de-dupe it to be more efficient)
		return filters.filter((f, index) => index === 0 || currencyTypes.indexOf(f.value) > -1);
	}

	public filterAndSearchList(filter: string, search: string): void {
		let list = this.applyFilter(this.allTradables, filter);

		if (search) {
			list = this.applySearch(list, search);
		}

		this.tradableList$.next(list);
	}

	private applyFilter(list: Tradable[], filter: string): Tradable[] {
		if (!filter) {
			return list;
		}

		return [...list].filter((t) => t.getCurrencyType() === filter);
	}

	private applySearch(list: Tradable[], search: string): Tradable[] {
		return Helper.filterBySearch(list, search, ["name", "symbol"]);
	}

	private getSellTradables(isLimitOrder: boolean = false): Tradable[] {
		let eligibleTradables = [];

		this.tradableWalletListService.list.filter((w) => {
			const tradable: Tradable = this.tradableListService.getItemById(w.getCurrencyId());
			if (!tradable) {
				return false;
			}

			let hasAboveMinSell: boolean = false;
			if (tradable instanceof Index) {
				hasAboveMinSell = w.getBalance() > 0 && w.getBalance() >= tradable.getMinSell();
			} else {
				// Index-wallet balance is already returned in EUR
				hasAboveMinSell =
					w.getBalance() > 0 && w.getBalance() * tradable.getAvgPrice() >= tradable.getMinSell();
			}
			if (isLimitOrder) {
				if (
					tradable.getAssetSettings()?.isPriceOrderAvailable() &&
					hasAboveMinSell &&
					tradable.isSellActive() &&
					!tradable.isIndexOnly() &&
					w.isDefault()
				) {
					eligibleTradables.push(tradable);
					return true;
				}
			} else {
				if (hasAboveMinSell && tradable.isSellActive() && !tradable.isIndexOnly() && w.isDefault()) {
					eligibleTradables.push(tradable);
					return true;
				}
			}
			return false;
		});

		return eligibleTradables;
	}
}
